import React, { useEffect, useState } from 'react';

import classNames from 'classnames';

import SupplierContractsIndexTableRow from './SupplierContractsIndexTableRow';
import SupplierContractTermCreateModal from './terms/SupplierContractTermCreateModal';

import { apiClient } from 'Helpers/network';

import BulkDeleteButtons from 'CommonComponents/BulkDeleteButtons';
import Button from 'CommonComponents/Button';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import SelectField from 'CommonComponents/SelectField';
import TableFilterModal from 'CommonComponents/TableFilterModal';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';
import TableSearchInput from 'CommonComponents/TableSearchInput';

import useBulkDelete from 'Hooks/useBulkDelete';
import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';
import usePagination from 'Hooks/usePagination';
import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';
import { useUsers } from 'Hooks/useUsers';

import FileExportIcon from 'Assets/icons/file-export.svg';

export default function SupplierContractsIndexTable(props) {
    const queryParams = new URLSearchParams(window.location.search);

    const [supplierContract, setSupplierContract] = useState([]);
    const [supplierContractTerms, setSupplierContractTerms] = useState(null);
    const [supplierContractTermAttachment, setSupplierContractTermAttachment] = useState(null);
    const [isLoading, setIsLoading] = useState({});
    const [searchValue, setSearchValue] = useState('');
    const [currentContract, setCurrentContract] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedOwner, setSelectedOwner] = useState(null);

    const supplierContractTermCreateModal = useModal();

    const toast = useToast();

    const { users } = useUsers();

    const confirm = useConfirmationModal();

    const { toggleSelecting, selecting, selectedItems, toggleItem } = useBulkDelete();

    const bulkDeleteContractsRequest = useQuery({
        enabled: false,
        queryFn: ({ ids, all }) => apiClient.delete(props.supplierContractBulkDeleteUrl, { data: { ids, all } }),
        successFn: () => {
            toast.success('Items removed successfully');
            props.refreshContractTables();
            toggleSelecting();
        },
    });

    useEffect(() => {
        fetchSupplierContracts();
    }, []);

    useEffect(() => {
        const contractId = queryParams.get('contract_id');

        if (contractId && supplierContract) {
            const contract = supplierContract?.find((item) => item.id == contractId);

            if (contract) {
                onEditTerms(contract);
            }
        }
    }, [supplierContract]);

    const onPageChange = () => {
        fetchSupplierContracts();
    };

    const pagination = usePagination({
        perPage: 25,
        onChange: onPageChange,
    });

    const { to, from, total, lastPage, perPage, page } = pagination;

    const pageChange = (data) => {
        pagination.setPage(+data.selected + 1);
    };

    const fetchSupplierContracts = () => {
        loading('supplier_contracts');

        axios
            .get(props.supplierContractsIndexUrl, {
                params: {
                    ...(props.forDeletedContracts ? { cancelled_contracts: true } : {}),
                    ...(props.forClosedContracts ? { closed_contracts: true } : {}),
                    ...(searchValue.length > 0 ? { search: searchValue } : {}),
                    ...(selectedDepartment ? { department_id: selectedDepartment.id } : {}),
                    ...(selectedOwner ? { owner_id: selectedOwner.value } : {}),
                    per_page: pagination.perPage,
                    page: pagination.page,
                },
            })
            .then((res) => {
                const { data, meta } = res.data;
                setSupplierContract(data);
                pagination.setTotal(meta.total);
                pagination.setLastPage(meta.last_page);
                pagination.setFrom(meta.from);
                pagination.setTo(meta.to);
                loading('supplier_contracts', false);
            })
            .catch((error) => {
                setSupplierContract([]);
                loading('supplier_contracts', false);
            });
    };

    const fetchSupplierContractTerms = (contractId) => {
        const url = props.supplierContractTermsIndexUrl.replace('CONTRACT_ID', contractId);
        loading('supplier_contract_terms');

        axios
            .get(url)
            .then(({ data }) => {
                setSupplierContractTerms(data);
            })
            .catch((error) => console.error(error))
            .finally(() => loading('supplier_contract_terms', false));
    };

    const deleteAttachment = (attachment) => {
        setSupplierContractTermAttachment(attachment);
        confirm({
            title: $t('Delete attachment'),
            message: $t('Are you sure you want to delete {{name}}?', { name: attachment?.file_name }),
            onConfirm: () => triggerDeleteAttachment(),
        });
    };

    const downloadAttachment = (e, attachment) => {
        e.preventDefault();
        const url = props.downloadSupplierContractsTermAttachmentUrl
            .replace('CONTRACT_ID', currentContract?.id)
            .replace('TERM_ID', attachment.term_id)
            .replace('ATTACHMENT_ID', attachment.id);

        axios({
            url: url,
            method: 'GET',
            responseType: 'blob',
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', attachment.file_name);
            document.body.appendChild(link);
            link.click();
        });
    };

    const handleChange = (event) => {
        setSearchValue(event.target.value);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            if (pagination.page === 1) {
                // already on page 1 → just fetch directly
                fetchSupplierContracts();
            } else {
                // jump to page 1 → this will trigger onChange effect
                pagination.setPage(1);
            }
        }
    };

    const loading = (key, value = true) => {
        setIsLoading((pervLoading) => ({ ...pervLoading, [key]: value }));
    };

    const onAddTerms = (contract) => {
        setSupplierContractTerms(null);
        setCurrentContract(contract);
        supplierContractTermCreateModal.show();
    };

    const onViewTerms = (contract) => {
        window.location.href = props.supplierContractTermsIndexUrl.replace('CONTRACT_ID', contract.id);
    };

    const onEditTerms = (contract) => {
        // Drop the previous contract's terms first: a failed fetch leaves them in place otherwise,
        // and the modal would open on this contract showing the last one's values.
        setSupplierContractTerms(null);
        setCurrentContract(contract);
        fetchSupplierContractTerms(contract.id);
        supplierContractTermCreateModal.show();
    };

    const onDeleteTerms = (contract) => {
        setCurrentContract(contract);
        fetchSupplierContractTerms(contract.id);
        confirm({
            title: $t('Delete Contract Terms'),
            message: $t('Are you sure you want to delete terms of contract {{ref}}?', { ref: contract?.contract_reference }),
            onConfirm: () => triggerDeleteTerm(contract),
        });
    };

    const onDeleteContract = (contract) => {
        setCurrentContract(contract);
        if (props.forDeletedContracts) {
            confirm({
                title: $t('Delete Contract'),
                message: $t('Are you sure you want to permanently delete contract {{ref}}?', { ref: contract?.contract_reference }),
                onConfirm: () => triggerDeleteContract(contract),
            });
        } else {
            confirm({
                title: $t('Delete Contract'),
                message: $t('Are you sure you want to delete contract {{ref}}?', {
                    ref: contract?.contract_reference,
                }),
                warning: $t(
                    'Deleting a contract will also delete commitments linked to this contract. The quantities committed will be added back to the current uncommitted quantity and inherit the current uncommitted price. You may need to go back into the line item and adjust the budget accordingly.',
                ),
                onConfirm: () => triggerDeleteContract(contract),
            });
        }
    };

    const onBulkDeleteContracts = () => {
        confirm({
            title: $t('Delete Contracts'),
            message: selecting ? $t('Are you sure you want to delete the selected contracts?') : $t('Are you sure you want to delete all contracts?'),
            warning: $t('Deleting a contract will also delete commitments linked to this contract'),
            onConfirm: () => triggerBulkDeleteContracts(),
        });
    };

    const triggerDeleteAttachment = () => {
        const url = props.deleteSupplierContractsTermAttachmentUrl
            .replace('SUPPLIER_CONTRACT_ID', supplierContractTerms?.supplier_contract_id)
            .replace('TERM_ID', supplierContractTerms?.id)
            .replace('ATTACHMENT_ID', supplierContractTermAttachment.id);

        axios
            .delete(url)
            .then(() => {
                fetchSupplierContractTerms(currentContract?.id);
            })
            .catch(() => toast.error($t('An error occurred, please try again')))
            .finally(() => toast.success($t('{{name}} could not be deleted', { name: supplierContractTermAttachment?.file_name })));
    };

    const triggerDeleteTerm = (contract) => {
        const url = props.supplierContractTermDeleteUrl
            .replace('SUPPLIER_CONTRACT_ID', supplierContractTerms?.supplier_contract_id)
            .replace('TERM_ID', supplierContractTerms?.id);

        axios
            .delete(url)
            .then(() => {
                fetchSupplierContracts();
            })
            .catch(() => toast.error($t('An error occurred, please try again')))
            .finally(() => toast.success($t('Contract {{ref}} terms deleted successfully', { ref: contract?.contract_reference })));
    };

    const triggerDeleteContract = (contract) => {
        const url = props.supplierContractDeleteUrl.replace('CONTRACT_ID', contract?.id);

        axios
            .delete(url)
            .then(() => {
                props.refreshContractTables();
            })
            .catch(() => toast.error($t('An error occurred, please try again')))
            .finally(() => toast.success($t('Contract {{ref}} deleted successfully', { ref: contract?.contract_reference })));
    };

    const triggerBulkDeleteContracts = () => {
        let ids = selectedItems;

        if (selecting && !selectedItems.length) {
            return;
        }

        bulkDeleteContractsRequest.refetch({ ids, all: !selecting });
    };

    const getTableTitle = () => {
        let title = $t('Expenditure Contracts');
        if (props.forDeletedContracts) {
            title = $t('Deleted Expenditure Contracts');
        } else if (props.forClosedContracts) {
            title = $t('Closed Expenditure Contracts');
        }

        return title;
    };

    const handleFilterApply = () => {
        if (pagination.page === 1) {
            // already on page 1 → just fetch directly
            fetchSupplierContracts();
        } else {
            // jump to page 1 → this will trigger onChange effect
            pagination.setPage(1);
        }
    };

    return (
        <>
            <div className="table__card">
                <div className="tw-flex tw-items-end tw-justify-between tw-mb-6">
                    <div className="tw-flex tw-flex-col tw-gap-y-1">
                        <h3 className="table__header">{getTableTitle()}</h3>
                        <div className="table__pagination-info">
                            <PaginationInfo to={to} from={from} total={total} />
                            <TablePagination lastPage={lastPage} pageChange={pageChange} currentPage={page} />
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-x-2.5">
                        <PerPageSelect
                            value={perPage}
                            onChange={(e) => {
                                pagination.setPerPage(parseInt(e.target.value));
                            }}
                        />
                        <TableSearchInput searchValue={searchValue} handleChange={handleChange} handleKeyPress={handleKeyPress} />
                        <BulkDeleteButtons
                            selecting={selecting}
                            selectedCount={selectedItems.length}
                            itemsCount={supplierContract.length}
                            loading={bulkDeleteContractsRequest.loading}
                            onDelete={() => onBulkDeleteContracts()}
                            toggleSelecting={toggleSelecting}
                        />
                        <div>
                            <TableFilterModal headerText={$t('Filters')} onApply={handleFilterApply} modalBodyClassNames="tw-w-128">
                                <div className="tw-flex tw-flex-col tw-gap-3">
                                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                        <label htmlFor="department" className="text-input__label">
                                            {$t('Select a department')}
                                        </label>
                                        <SelectField
                                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                                            options={props.departmentsOptions}
                                            value={selectedDepartment}
                                            isDisabled={isLoading.supplier_contracts}
                                            onChange={(department) => setSelectedDepartment(department)}
                                            backgroundColor="#e4e4e480"
                                            height={32}
                                            clearable
                                            menuPortalTarget={null}
                                        />
                                    </div>
                                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                        <label htmlFor="owner" className="text-input__label">
                                            {$t('Select an owner')}
                                        </label>
                                        <SelectField
                                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                                            options={props.ownersOptions}
                                            value={selectedOwner}
                                            isDisabled={isLoading.supplier_contracts}
                                            onChange={(owner) => setSelectedOwner(owner)}
                                            backgroundColor="#e4e4e480"
                                            height={32}
                                            clearable
                                            menuPortalTarget={null}
                                        />
                                    </div>
                                </div>
                            </TableFilterModal>
                        </div>
                    </div>
                </div>
                <div id={'supplier-contracts-table'} className="inbud-table_wrapper">
                    <table className="inbud-table tw-table-fixed">
                        <thead className="tw-capitalize">
                            <tr>
                                <th rowSpan={props.forDeletedContracts ? 1 : 2} className={classNames('tw-w-8', { 'tw-hidden': !selecting })}>
                                    {!!selectedItems.length && (
                                        <div className="tw-rounded-full tw-h-4 tw-w-4 tw-flex tw-items-center tw-justify-center tw-text-2xs tw-text-white tw-bg-red-75">
                                            {selectedItems.length}
                                        </div>
                                    )}
                                </th>
                                <th className="tw-w-28">{$t('Contract')}</th>
                                <th className="tw-w-14">{$t('Signed')}</th>
                                <th className="tw-w-40">{$t('Supplier Name')}</th>
                                {!props.forDeletedContracts && <th className="tw-w-80">{$t('Department')}</th>}
                                <th className="tw-w-32">{$t('Contracted')}</th>
                                {!props.forDeletedContracts && (
                                    <>
                                        <th className="tw-w-24">{$t('Actuals')}</th>
                                        <th className="tw-w-24">{$t('Variance')}</th>
                                        <th className="tw-border-r-2 tw-w-40">{$t('Owner')}</th>
                                        <th className="!tw-pb-1.5 tw-w-40 tw-border-r-2 !tw-text-center">{$t('Payment milestones')}</th>
                                        <th className="!tw-pb-1.5 tw-w-40 tw-border-r-2 !tw-text-center">{$t('Other milestones')}</th>
                                    </>
                                )}
                                <th className="tw-w-72">{$t('Actions')}</th>
                            </tr>
                        </thead>
                        {!isLoading.supplier_contracts && supplierContract.length > 0 && (
                            <tbody>
                                {supplierContract.map((supplierContract) => {
                                    return (
                                        <SupplierContractsIndexTableRow
                                            key={supplierContract.id}
                                            supplierContract={supplierContract}
                                            displayApprovalStatuses={props.displayApprovalStatuses}
                                            updateSignedContractUrl={props.updateSignedContractUrl}
                                            cancelPOUrl={props.cancelPOUrl}
                                            selecting={selecting}
                                            selectedIds={selectedItems}
                                            isSupplierPortalEnabled={props.isSupplierPortalEnabled}
                                            onSelect={() => toggleItem(supplierContract.id)}
                                            onAddTerms={onAddTerms}
                                            onViewTerms={onViewTerms}
                                            onEditTerms={onEditTerms}
                                            onDeleteTerms={onDeleteTerms}
                                            onDeleteContract={onDeleteContract}
                                            fetchSupplierContracts={fetchSupplierContracts}
                                            forDeletedContracts={props.forDeletedContracts}
                                        />
                                    );
                                })}
                            </tbody>
                        )}
                        {isLoading.supplier_contracts && (
                            <tbody>
                                <TableRowSkeleton rows={10} columns={props.forDeletedContracts ? 4 : 11} />
                            </tbody>
                        )}
                        {!isLoading.supplier_contracts && supplierContract.length === 0 && (
                            <tbody>
                                <tr>
                                    <td colSpan={!props.forDeletedContracts ? 11 : 4}>
                                        {!props.forDeletedContracts
                                            ? $t('No Contracts found, you can add Contracts Assigning them to Committed Expenditures')
                                            : $t('No Contracts found')}
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                </div>
                {!props.forDeletedContracts && (
                    <div className="tw-flex tw-justify-end tw-gap-x-2 tw-mt-4">
                        <Button variant="primary" as="a" href={props.exportToFile} data-action="navigate">
                            <FileExportIcon className="tw-w-3 tw-h-3" />
                            {$t('Export to file')}
                        </Button>
                    </div>
                )}
            </div>

            <SupplierContractTermCreateModal
                modal={supplierContractTermCreateModal}
                isLoading={isLoading.supplier_contract_terms}
                storeUrl={props.supplierContractTermStoreUrl}
                updateUrl={props.supplierContractTermUpdateUrl}
                updateQuantitiesUrl={props.updateQuantitiesUrl}
                contract={currentContract}
                terms={supplierContractTerms}
                users={users}
                onCreated={() => fetchSupplierContracts()}
                onDeleteAttachment={deleteAttachment}
                onDownloadAttachment={downloadAttachment}
                onQuantitiesUpdated={() => fetchSupplierContractTerms(currentContract?.id)}
                onRefreshContracts={fetchSupplierContracts}
                eventStartDate={props.eventStartDate}
                eventEndDate={props.eventEndDate}
            />
        </>
    );
}
